/**
 * Widget Registry - Centralized widget definitions for dashboard customization
 *
 * This registry defines all available widgets that can be added to department dashboards.
 * Widgets are categorized as 'common' (available to all) or 'department' (specific to certain departments).
 */

import { LucideIcon } from 'lucide-react'
import {
    Building2,
    Calendar,
    CalendarDays,
    Bell,
    Target,
    Users,
    TrendingUp,
    AlertTriangle,
    Clock,
    FileText,
    // BarChart3, // Reserved for future use
    PieChart,
    Ticket,
    Shield,
    Phone,
    // Briefcase, // Reserved for future use
    UserCheck,
    FileWarning,
    Receipt,
    Quote,
    LineChart,
} from 'lucide-react'

// Grid sizes for widget colSpan
export type GridSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

// Department identifiers matching the routing system
export type DepartmentType =
    | 'ALL'
    | 'TECHNOLOGY'
    | 'DEVELOPMENT'
    | 'SALES'
    | 'SALE PARTNER'
    | 'ACCOUNT'
    | 'FINANCE'
    | 'HUMAN RESOURCE'
    | 'MARKETING'
    | 'BOARD OF DIRECTORS'
    | 'BOARD OF MANAGEMENT'

export interface WidgetDefinition {
    id: string
    nameKey: string // i18n key for widget name
    descriptionKey: string // i18n key for widget description
    icon: LucideIcon
    category: 'common' | 'department'
    departments: DepartmentType[] // ['ALL'] means available to all departments
    defaultColSpan: GridSize
    // Component import path - used for lazy loading
    componentPath: string
}

/**
 * Common widgets available to all departments
 */
const COMMON_WIDGETS: WidgetDefinition[] = [
    {
        id: 'quote_of_the_day',
        nameKey: 'widget.quote_of_the_day.name',
        descriptionKey: 'widget.quote_of_the_day.description',
        icon: Quote,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 12,
        componentPath: 'shared/QuoteOfTheDay',
    },
    {
        id: 'business',
        nameKey: 'widget.business.name',
        descriptionKey: 'widget.business.description',
        icon: Building2,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 3,
        componentPath: 'shared/Business',
    },
    {
        id: 'workday',
        nameKey: 'widget.workday.name',
        descriptionKey: 'widget.workday.description',
        icon: CalendarDays,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 3,
        componentPath: 'shared/WorkDays',
    },
    {
        id: 'my_workdays',
        nameKey: 'widget.my_workdays.name',
        descriptionKey: 'widget.my_workdays.description',
        icon: Calendar,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 3,
        componentPath: 'shared/MyWorkdays',
    },
    {
        id: 'notification',
        nameKey: 'widget.notification.name',
        descriptionKey: 'widget.notification.description',
        icon: Bell,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 6,
        componentPath: 'shared/Notifications',
    },
    {
        id: 'events',
        nameKey: 'widget.events.name',
        descriptionKey: 'widget.events.description',
        icon: Calendar,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 3,
        componentPath: 'shared/Events',
    },
    {
        id: 'revenue',
        nameKey: 'widget.revenue.name',
        descriptionKey: 'widget.revenue.description',
        icon: Target,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 6,
        componentPath: 'shared/TargetRevenue',
    },
    {
        id: 'leads-statistics',
        nameKey: 'widget.leads_statistics.name',
        descriptionKey: 'widget.leads_statistics.description',
        icon: Users,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 3,
        componentPath: 'shared/LeadsStatistics',
    },
    {
        id: 'sla-customers',
        nameKey: 'widget.sla_customers.name',
        descriptionKey: 'widget.sla_customers.description',
        icon: Shield,
        category: 'common',
        departments: ['ALL'],
        defaultColSpan: 4,
        componentPath: 'shared/widgets/SLACustomersWidget',
    },
]

/**
 * Technology/Development department widgets
 */
const TECH_WIDGETS: WidgetDefinition[] = [
    {
        id: 'tickets',
        nameKey: 'widget.tickets.name',
        descriptionKey: 'widget.tickets.description',
        icon: Ticket,
        category: 'department',
        departments: ['TECHNOLOGY', 'DEVELOPMENT'],
        defaultColSpan: 3,
        componentPath: 'TechDashboard/widgets/TechnicalTicketsWidget',
    },
    {
        id: 'sla-dashboard',
        nameKey: 'widget.sla_dashboard.name',
        descriptionKey: 'widget.sla_dashboard.description',
        icon: Shield,
        category: 'department',
        departments: ['TECHNOLOGY', 'DEVELOPMENT'],
        defaultColSpan: 3,
        componentPath: 'TechDashboard/widgets/SLADashboardWidget',
    },
    {
        id: 'oncall-timeline',
        nameKey: 'widget.oncall_timeline.name',
        descriptionKey: 'widget.oncall_timeline.description',
        icon: Phone,
        category: 'department',
        departments: ['TECHNOLOGY', 'DEVELOPMENT'],
        defaultColSpan: 6,
        componentPath: 'TechDashboard/widgets/OncallTimelineWidget',
    },
]

/**
 * Sales department widgets
 */
const SALES_WIDGETS: WidgetDefinition[] = [
    {
        id: 'conversion-funnel',
        nameKey: 'widget.conversion_funnel.name',
        descriptionKey: 'widget.conversion_funnel.description',
        icon: TrendingUp,
        category: 'department',
        departments: ['SALES', 'SALE PARTNER'],
        defaultColSpan: 6,
        componentPath: 'SalesDashboard/widgets/ConversionFunnelWidget',
    },
    {
        id: 'daily-input',
        nameKey: 'widget.daily_input.name',
        descriptionKey: 'widget.daily_input.description',
        icon: FileText,
        category: 'department',
        departments: ['SALES', 'SALE PARTNER'],
        defaultColSpan: 6,
        componentPath: 'SalesDashboard/widgets/DailyInputWidget',
    },
    {
        id: 'alerts',
        nameKey: 'widget.alerts.name',
        descriptionKey: 'widget.alerts.description',
        icon: AlertTriangle,
        category: 'department',
        departments: ['SALES', 'SALE PARTNER', 'ACCOUNT'],
        defaultColSpan: 3,
        componentPath: 'SalesDashboard/widgets/SalesAlertsWidget',
    },
    {
        id: 'sales-oncall-schedule',
        nameKey: 'widget.sales_oncall_schedule.name',
        descriptionKey: 'widget.sales_oncall_schedule.description',
        icon: Phone,
        category: 'department',
        departments: ['SALES', 'SALE PARTNER'],
        defaultColSpan: 6,
        componentPath: 'SalesDashboard/widgets/SalesOncallScheduleWidget',
    },
]

/**
 * Account department widgets
 */
const ACCOUNT_WIDGETS: WidgetDefinition[] = [
    {
        id: 'service-trend',
        nameKey: 'widget.service_trend.name',
        descriptionKey: 'widget.service_trend.description',
        icon: LineChart,
        category: 'department',
        departments: ['ACCOUNT'],
        defaultColSpan: 6,
        componentPath: 'AccountDashboard/widgets/ServiceTrendWidget',
    },
    {
        id: 'cancellation-rate',
        nameKey: 'widget.cancellation_rate.name',
        descriptionKey: 'widget.cancellation_rate.description',
        icon: TrendingUp,
        category: 'department',
        departments: ['ACCOUNT'],
        defaultColSpan: 6,
        componentPath: 'AccountDashboard/widgets/CancellationRateWidget',
    },
    {
        id: 'waiting-confirm',
        nameKey: 'widget.waiting_confirm.name',
        descriptionKey: 'widget.waiting_confirm.description',
        icon: Clock,
        category: 'department',
        departments: ['ACCOUNT'],
        defaultColSpan: 3,
        componentPath: 'AccountDashboard/widgets/WaitingConfirmWidget',
    },
    {
        id: 'invoice-waiting',
        nameKey: 'widget.invoice_waiting.name',
        descriptionKey: 'widget.invoice_waiting.description',
        icon: Receipt,
        category: 'department',
        departments: ['ACCOUNT'],
        defaultColSpan: 3,
        componentPath: 'AccountDashboard/widgets/InvoiceWaitingWidget',
    },
]

/**
 * Finance department widgets
 */
const FINANCE_WIDGETS: WidgetDefinition[] = [
    {
        id: 'revenue-allocation',
        nameKey: 'widget.revenue_allocation.name',
        descriptionKey: 'widget.revenue_allocation.description',
        icon: PieChart,
        category: 'department',
        departments: ['FINANCE'],
        defaultColSpan: 4,
        componentPath: 'FinanceDashboard/widgets/RevenueAllocationWidget',
    },
    {
        id: 'invoicable',
        nameKey: 'widget.invoicable.name',
        descriptionKey: 'widget.invoicable.description',
        icon: FileText,
        category: 'department',
        departments: ['FINANCE'],
        defaultColSpan: 4,
        componentPath: 'FinanceDashboard/widgets/InvoicableWidget',
    },
    {
        id: 'contract-alerts',
        nameKey: 'widget.contract_alerts.name',
        descriptionKey: 'widget.contract_alerts.description',
        icon: FileWarning,
        category: 'department',
        departments: ['FINANCE'],
        defaultColSpan: 4,
        componentPath: 'FinanceDashboard/widgets/ContractAlertsWidget',
    },
]

/**
 * HR department widgets
 */
const HR_WIDGETS: WidgetDefinition[] = [
    {
        id: 'late_employees',
        nameKey: 'widget.late_employees.name',
        descriptionKey: 'widget.late_employees.description',
        icon: Clock,
        category: 'department',
        departments: ['HUMAN RESOURCE'],
        defaultColSpan: 3,
        componentPath: 'HRDashboard/widgets/LateEmployeesWidget',
    },
    {
        id: 'interview_schedule',
        nameKey: 'widget.interview_schedule.name',
        descriptionKey: 'widget.interview_schedule.description',
        icon: UserCheck,
        category: 'department',
        departments: ['HUMAN RESOURCE'],
        defaultColSpan: 3,
        componentPath: 'HRDashboard/widgets/InterviewScheduleWidget',
    },
    // TODO: temporarily disabled — re-enable when ready
    // {
    //     id: 'employee_wall',
    //     nameKey: 'widget.employee_wall.name',
    //     descriptionKey: 'widget.employee_wall.description',
    //     icon: Users,
    //     category: 'department',
    //     departments: ['HUMAN RESOURCE'],
    //     defaultColSpan: 12,
    //     componentPath: 'shared/widgets/EmployeeWallWidget',
    // },
]

/**
 * All widgets combined
 */
export const WIDGET_REGISTRY: WidgetDefinition[] = [
    ...COMMON_WIDGETS,
    ...TECH_WIDGETS,
    ...SALES_WIDGETS,
    ...ACCOUNT_WIDGETS,
    ...FINANCE_WIDGETS,
    ...HR_WIDGETS,
]

/**
 * Executive departments that can access all widgets
 */
const EXECUTIVE_DEPARTMENTS: DepartmentType[] = [
    'BOARD OF DIRECTORS',
    'BOARD OF MANAGEMENT',
]

/**
 * Get widgets available for a specific department
 * @param department - The department name
 * @returns Array of widget definitions available to the department
 */
export const getWidgetsForDepartment = (
    department: string
): WidgetDefinition[] => {
    const normalizedDept = department.toUpperCase() as DepartmentType

    // Executive sees all widgets
    if (EXECUTIVE_DEPARTMENTS.includes(normalizedDept)) {
        return WIDGET_REGISTRY
    }

    return WIDGET_REGISTRY.filter(
        (widget) =>
            widget.departments.includes('ALL') ||
            widget.departments.includes(normalizedDept)
    )
}

/**
 * Get available widgets (widgets not currently on dashboard)
 * @param department - The department name
 * @param activeWidgetIds - IDs of widgets currently on dashboard
 * @returns Widgets that can be added from the store
 */
export const getAvailableWidgets = (
    department: string,
    activeWidgetIds: string[]
): WidgetDefinition[] => {
    const departmentWidgets = getWidgetsForDepartment(department)
    return departmentWidgets.filter(
        (widget) => !activeWidgetIds.includes(widget.id)
    )
}

/**
 * Get widget by ID
 */
export const getWidgetById = (id: string): WidgetDefinition | undefined => {
    return WIDGET_REGISTRY.find((widget) => widget.id === id)
}

/**
 * Default widgets for each department type
 * These are shown when user resets their layout
 */
export const DEFAULT_WIDGETS: Record<string, string[]> = {
    TECHNOLOGY: [
        'quote_of_the_day',
        'tickets',
        'sla-dashboard',
        'my_workdays',
        'oncall-timeline',
        'notification',
        'business',
        'workday',
    ],
    DEVELOPMENT: [
        'quote_of_the_day',
        'tickets',
        'sla-dashboard',
        'my_workdays',
        'oncall-timeline',
        'notification',
        'business',
        'workday',
    ],
    SALES: [
        'quote_of_the_day',
        'revenue',
        'daily-input',
        'conversion-funnel',
        'leads-statistics',
        'alerts',
        'sales-oncall-schedule',
        'business',
        'my_workdays',
        'notification',
        'workday',
        'events',
        'sla-customers',
    ],
    'SALE PARTNER': [
        'quote_of_the_day',
        'revenue',
        'daily-input',
        'conversion-funnel',
        'leads-statistics',
        'alerts',
        'sales-oncall-schedule',
        'business',
        'my_workdays',
        'notification',
        'workday',
        'events',
        'sla-customers',
    ],
    ACCOUNT: [
        'quote_of_the_day',
        'revenue',
        'my_workdays',
        'workday',
        'service-trend',
        'cancellation-rate',
        'alerts',
        'waiting-confirm',
        'invoice-waiting',
        'business',
        'notification',
        'events',
        'sla-customers',
    ],
    FINANCE: [
        'quote_of_the_day',
        'revenue',
        'business',
        'workday',
        'revenue-allocation',
        'invoicable',
        'contract-alerts',
        'my_workdays',
        'notification',
        'events',
    ],
    'HUMAN RESOURCE': [
        'quote_of_the_day',
        'my_workdays',
        'late_employees',
        'workday',
        'interview_schedule',
        'notification',
        'business',
        'events',
        'employee_wall',
    ],
    MARKETING: [
        'quote_of_the_day',
        'revenue',
        'leads-statistics',
        'my_workdays',
        'business',
        'workday',
        'events',
        'notification',
    ],
    'BOARD OF DIRECTORS': [
        'quote_of_the_day',
        'revenue',
        'business',
        'my_workdays',
        'notification',
        'events',
        'workday',
    ],
    'BOARD OF MANAGEMENT': [
        'quote_of_the_day',
        'revenue',
        'business',
        'my_workdays',
        'notification',
        'events',
        'workday',
    ],
    // Default fallback
    DEFAULT: [
        'quote_of_the_day',
        'business',
        'my_workdays',
        'notification',
        'events',
        'workday',
    ],
}

/**
 * Get default widget IDs for a department
 */
export const getDefaultWidgets = (department: string): string[] => {
    const normalizedDept = department.toUpperCase()
    return DEFAULT_WIDGETS[normalizedDept] || DEFAULT_WIDGETS.DEFAULT
}
